import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listFiles, getFileCount } from "../../../services/files";
import type { FileItem, FileCountStats } from "../../../services/files";
import { listProjects, countProjects } from "../../../services/projects";
import type { ProjectCountStats } from "../../../services/projects";
import { formatBeijingLongDate } from "../../../utils/datetime";
import type { ProjectItem } from "../../../services/projects";
import { getChatHistory, getChatHistoryCount } from "../../../services/chat";
import type { ChatHistoryItem } from "../../../services/chat";

// ── Helpers ──────────────────────────────────────────────────────────────────

import { getIndexStatusLabel } from "../../../utils/fileStatus";
import RIcon from "../../../components/RIcon";


function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExt(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

// ── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
  sub: string;
  subColor?: string;
  trend?: "up" | "down" | "neutral";
}

function StatCard({ icon, iconBg, iconColor, label, value, sub, subColor, trend }: StatCardProps) {
  const trendIcon =
    trend === "up" ? "ri-arrow-up-line" :
    trend === "down" ? "ri-arrow-down-line" : "";
  const trendColor =
    trend === "up" ? "text-emerald-500" :
    trend === "down" ? "text-red-400" : "";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4 hover:shadow-sm transition-shadow">
      <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <i className={`${icon} text-xl ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[#718096] mb-1">{label}</p>
        <p className="text-2xl font-bold text-[#1A2B3C] leading-none mb-1.5">{value}</p>
        <p className={`text-xs flex items-center gap-1 ${subColor || "text-[#718096]"}`}>
          {trendIcon && <i className={`${trendIcon} ${trendColor}`} />}
          {sub}
        </p>
      </div>
    </div>
  );
}

// ── Mini Bar Chart ───────────────────────────────────────────────────────────

const DAY_LABELS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

function buildWeeklyTrend(chats: ChatHistoryItem[]): { counts: number[]; labels: string[] } {
  const today = new Date();
  const counts: number[] = [];
  const labels: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const count = chats.filter((c) => c.created_at.startsWith(dateStr)).length;
    counts.push(count);
    labels.push(i === 0 ? "今天" : DAY_LABELS[d.getDay()]);
  }
  return { counts, labels };
}

function MiniBarChart({ counts, labels }: { counts: number[]; labels: string[] }) {
  const max = Math.max(...counts, 1);
  return (
    <div className="flex items-end gap-1.5 h-16">
      {counts.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className={`w-full rounded-t-sm transition-all ${i === counts.length - 1 ? "bg-[#0EA5A0]" : "bg-[#0EA5A0]/30"}`}
            style={{ height: `${(v / max) * 56}px`, minHeight: v > 0 ? "4px" : "0px" }}
          />
          <span className="text-[10px] text-[#718096] whitespace-nowrap">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

// ── Index Progress Ring ──────────────────────────────────────────────────────

function IndexRing({ pct }: { pct: number }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width="72" height="72" viewBox="0 0 72 72">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#F4F8FB" strokeWidth="7" />
      <circle
        cx="36" cy="36" r={r} fill="none"
        stroke="#0EA5A0" strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ / 4}
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
      <text x="36" y="41" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1A2B3C">
        {pct}%
      </text>
    </svg>
  );
}

// ── Quick Action Card ────────────────────────────────────────────────────────

interface QuickActionProps {
  to: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  desc: string;
}

function QuickAction({ to, icon, iconBg, iconColor, title, desc }: QuickActionProps) {
  return (
    <Link
      to={to}
      className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3.5 hover:border-[#0EA5A0]/40 hover:shadow-sm transition-all group cursor-pointer"
    >
      <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
        <i className={`${icon} text-lg ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#1A2B3C] group-hover:text-[#0EA5A0] transition-colors">{title}</p>
        <p className="text-xs text-[#718096] truncate">{desc}</p>
      </div>
      <div className="w-5 h-5 flex items-center justify-center ml-auto text-[#718096] group-hover:text-[#0EA5A0] transition-colors">
        <RIcon name="arrow-right-line" className="text-sm" />
      </div>
    </Link>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────────

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState<"files" | "chats">("files");
  const [allFiles, setAllFiles] = useState<FileItem[]>([]);
  const [allProjects, setAllProjects] = useState<ProjectItem[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [fileStats, setFileStats] = useState<FileCountStats | null>(null);
  const [projectStats, setProjectStats] = useState<ProjectCountStats | null>(null);
  const [chatTotal, setChatTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiHealthy, setAiHealthy] = useState(false);

  // 真实健康检测：每 30s 探一次 /health（后端 + 取最近一次 chatTotal 调用是否成功）
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const res = await fetch(`${(import.meta.env.VITE_API_BASE || "/api").replace(/\/+$/, "")}/health`, { method: "GET" });
        if (!cancelled) setAiHealthy(res.ok);
      } catch {
        if (!cancelled) setAiHealthy(false);
      }
    };
    probe();
    const t = setInterval(probe, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        // 概览的"总数"用轻量 count 接口；列表/图表只拉最近一部分（避免一次把全量数据都吞回来）
        const [filesRes, projRes, chatRes, fileCountRes, projCountRes, chatCountRes] = await Promise.allSettled([
          listFiles(0, 5),
          listProjects(),
          getChatHistory(0, 30),
          getFileCount(),
          countProjects(),
          getChatHistoryCount(),
        ]);

        if (filesRes.status === "fulfilled") setAllFiles(filesRes.value);
        if (projRes.status === "fulfilled") setAllProjects(projRes.value);
        if (chatRes.status === "fulfilled") setChatHistory(chatRes.value);
        if (fileCountRes.status === "fulfilled") setFileStats(fileCountRes.value);
        if (projCountRes.status === "fulfilled") setProjectStats(projCountRes.value);
        if (chatCountRes.status === "fulfilled") setChatTotal(chatCountRes.value.total);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // 总数优先用 count 接口返回值；count 接口没拿到再 fallback 到本地长度
  const totalFiles = fileStats?.total ?? allFiles.length;
  const indexedFiles = fileStats?.completed ?? allFiles.filter((f) => f.index_status === "completed").length;
  const indexPct = totalFiles > 0 ? Math.round((indexedFiles / totalFiles) * 100) : 0;
  const inProgressProjects = projectStats?.in_progress ?? allProjects.filter((p) => p.status === "进行中").length;
  const totalProjects = projectStats?.total ?? allProjects.length;
  const totalChats = chatTotal ?? chatHistory.length;

  const recentFiles = allFiles.slice(0, 5);
  const recentChats = chatHistory.slice(0, 5);

  const indexingCount =
    fileStats ? (fileStats.processing + fileStats.pending) : allFiles.filter((f) => f.index_status === "processing" || f.index_status === "pending").length;
  const failedCount = fileStats?.failed ?? allFiles.filter((f) => f.index_status === "failed").length;

  const { counts: trendCounts, labels: trendLabels } = buildWeeklyTrend(chatHistory);
  const todayChats = trendCounts[trendCounts.length - 1];

  const indexStatusColor: Record<string, string> = {
    "已完成": "text-emerald-600 bg-emerald-50",
    "索引中": "text-amber-600 bg-amber-50",
    "索引失败": "text-red-600 bg-red-50",
  };
  const fileTypeIcon: Record<string, string> = {
    pdf: "ri-file-pdf-line text-red-400",
    doc: "ri-file-word-line text-blue-400",
    docx: "ri-file-word-line text-blue-400",
    xls: "ri-file-excel-line text-green-500",
    xlsx: "ri-file-excel-line text-green-500",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <RIcon name="loader-4-line" className="animate-spin text-3xl text-[#0EA5A0]" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#1A2B3C] to-[#0EA5A0]/80 rounded-xl px-6 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-white mb-1">医疗知识智能化管理平台</h1>
          <p className="text-sm text-white/70">
            <RIcon name="calendar-line" className="mr-1" />{formatBeijingLongDate(new Date())}
            <span className={`ml-4 font-medium ${aiHealthy ? "text-[#AAFF45]" : "text-red-300"}`}>
              <RIcon name="radio-button-line" className={`mr-1 ${aiHealthy ? "animate-pulse" : ""}`} />
              {aiHealthy ? "AI 服务运行正常" : "AI 服务异常"}
            </span>
          </p>
        </div>
      </div>

      {/* Quick Actions（顶置） */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <QuickAction to="/workbench/ai-chat" icon="ri-chat-ai-line" iconBg="bg-[#0EA5A0]/10" iconColor="text-[#0EA5A0]" title="AI智能问答" desc="基于知识库精准解答" />
        <QuickAction to="/workbench/doc-gen" icon="ri-file-text-line" iconBg="bg-violet-50" iconColor="text-violet-500" title="智能文档生成" desc="一键生成标准化文档" />
        <QuickAction to="/workbench/files" icon="ri-folder-line" iconBg="bg-amber-50" iconColor="text-amber-500" title="文件管理" desc="上传与管理知识文档" />
        <QuickAction to="/workbench/projects" icon="ri-kanban-view" iconBg="bg-emerald-50" iconColor="text-emerald-600" title="项目中心" desc="追踪研发项目进度" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="ri-file-list-3-line"
          iconBg="bg-[#0EA5A0]/10"
          iconColor="text-[#0EA5A0]"
          label="知识库文件总数"
          value={totalFiles}
          sub={`${indexedFiles} 个已索引`}
          trend="up"
        />
        <StatCard
          icon="ri-database-2-line"
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="索引完成率"
          value={`${indexPct}%`}
          sub={`${indexedFiles}/${totalFiles} 已完成索引`}
          subColor="text-emerald-600"
          trend="up"
        />
        <StatCard
          icon="ri-chat-3-line"
          iconBg="bg-violet-50"
          iconColor="text-violet-500"
          label="历史问答"
          value={totalChats}
          sub={`今日 ${todayChats} 次`}
          subColor="text-violet-500"
        />
        <StatCard
          icon="ri-kanban-view"
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="进行中项目"
          value={inProgressProjects}
          sub={`共 ${totalProjects} 个项目`}
        />
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-1 bg-[#F4F8FB] rounded-lg p-1">
              {([["files", "最近文件"], ["chats", "最近问答"]] as const).map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 h-7 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab
                      ? "bg-white text-[#1A2B3C] font-medium shadow-sm"
                      : "text-[#718096] hover:text-[#1A2B3C]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Link
              to={activeTab === "files" ? "/workbench/files" : "/workbench/ai-chat"}
              className="text-xs text-[#0EA5A0] hover:underline cursor-pointer whitespace-nowrap"
            >
              查看全部 <RIcon name="arrow-right-line" />
            </Link>
          </div>

          <div className="divide-y divide-gray-50">
            {activeTab === "files"
              ? recentFiles.length === 0 ? (
                  <div className="py-10 text-center text-sm text-[#718096]">暂无文件</div>
                ) : recentFiles.map((f) => {
                  const ext = getFileExt(f.file_name);
                  const statusLabel = getIndexStatusLabel(f.index_status);
                  return (
                    <Link
                      key={f.id}
                      to={`/workbench/files/${f.id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-[#F4F8FB]/60 transition-colors group cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <RIcon name="file-line" className={`${fileTypeIcon[ext] || " text-gray-400"} text-lg`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1A2B3C] truncate group-hover:text-[#0EA5A0] transition-colors">{f.file_name}</p>
                        <p className="text-xs text-[#718096]">{f.created_at.slice(0, 10)} · {formatBytes(f.file_size)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${indexStatusColor[statusLabel] || "text-gray-500 bg-gray-100"}`}>
                        {statusLabel}
                      </span>
                    </Link>
                  );
                })
              : recentChats.length === 0 ? (
                  <div className="py-10 text-center text-sm text-[#718096]">暂无问答记录</div>
                ) : recentChats.map((c) => (
                  <Link
                    key={c.id}
                    to="/workbench/ai-chat"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-[#F4F8FB]/60 transition-colors group cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#0EA5A0]/10 flex items-center justify-center flex-shrink-0">
                      <RIcon name="chat-ai-line" className="text-[#0EA5A0] text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A2B3C] truncate group-hover:text-[#0EA5A0] transition-colors">{c.question}</p>
                      <p className="text-xs text-[#718096] truncate">{c.answer.slice(0, 80)}</p>
                    </div>
                    <span className="text-xs text-[#718096] flex-shrink-0">{c.created_at.slice(11, 16) || c.created_at.slice(0, 10)}</span>
                  </Link>
                ))
            }
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Index Overview */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-sm font-semibold text-[#1A2B3C] mb-4">索引进度概览</p>
            <div className="flex items-center gap-4">
              <IndexRing pct={indexPct} />
              <div className="space-y-2 flex-1">
                {[
                  { label: "已完成", count: indexedFiles, color: "bg-emerald-400" },
                  { label: "索引中", count: indexingCount, color: "bg-amber-400" },
                  { label: "索引失败", count: failedCount, color: "bg-red-400" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${item.color}`} />
                      <span className="text-[#718096]">{item.label}</span>
                    </div>
                    <span className="font-medium text-[#1A2B3C]">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Trend */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-[#1A2B3C]">本周问答趋势</p>
              <span className="text-xs text-[#0EA5A0] font-medium">今日 {todayChats} 次</span>
            </div>
            <MiniBarChart counts={trendCounts} labels={trendLabels} />
          </div>
        </div>
      </div>

      {/* Project Progress */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-[#1A2B3C]">项目进度一览</p>
          <Link to="/workbench/projects" className="text-xs text-[#0EA5A0] hover:underline cursor-pointer whitespace-nowrap">
            全部项目 <RIcon name="arrow-right-line" />
          </Link>
        </div>
        {allProjects.length === 0 ? (
          <p className="text-sm text-[#718096] text-center py-4">暂无项目</p>
        ) : (
          <div className="space-y-3">
            {allProjects.slice(0, 4).map((p) => {
              const statusColor: Record<string, string> = {
                "进行中": "text-[#0EA5A0] bg-[#0EA5A0]/10",
                "已完成": "text-emerald-600 bg-emerald-50",
                "暂停": "text-amber-600 bg-amber-50",
              };
              return (
                <div key={p.id} className="flex items-center gap-4">
                  <div className="w-36 min-w-0 flex-shrink-0">
                    <p className="text-sm text-[#1A2B3C] truncate">{p.name}</p>
                    <p className="text-xs text-[#718096] truncate">{p.leader ?? "待指派"} · {p.department ?? "待分配"}</p>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-full bg-[#0EA5A0] rounded-full transition-all duration-700"
                      style={{ width: `${p.progress}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[#1A2B3C] w-8 text-right flex-shrink-0">{p.progress}%</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor[p.status] || "text-gray-500 bg-gray-100"}`}>
                    {p.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
